'use client';

/**
 * **A form field wearing the attribute treatment instead of the form chrome**
 * (LF-6314, LF-6311 gap **A**).
 *
 * The same field, the same validation, the same money-typing behaviour — a
 * different box around it. Where {@link FormField}'s `form` variant stacks a
 * `text-sm font-medium` label above a grey-filled bordered input, this renders one
 * attribute row: the shared label column on the left, the editor in the value
 * cell on the right, on the row rhythm every other row of the surface keeps.
 *
 * ## Why this exists as a second file rather than a branch inside `FormField`
 *
 * `FormField` was already 362 lines against the frontend's 300-line guideline,
 * and four more chrome branches inside it would have made the one component that
 * every inventory form depends on the hardest file in the folder to read. The two
 * chromes are genuinely separate renders over one shared behaviour, so they are
 * separate files over one shared module (`./currency-input`).
 *
 * ## What it deliberately does not do: read mode
 *
 * There is no compact read-out here, and `FormField` keeps its own read row for
 * both variants on purpose. **A read-only attribute record has no defined
 * appearance** — that is LF-6311's gap **K**, still unsolved, and the whole point
 * of the LF-6314 prototype is to surface the gaps rather than quietly invent
 * answers to them. Inventing one in a shared production component would settle a
 * question this ticket is supposed to be *asking*.
 *
 * ## The tokens are imported, not retyped
 *
 * The row vocabulary comes from `./attribute-row` and every measurement under it
 * from `components/attributes/attribute-tokens` — the treatment's own executable rules,
 * promoted to production by LF-6304. That is the module's stated contract: *"A new
 * surface that wants this treatment imports them; it does not retype them."*
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_MUTED_INK_PLACEHOLDER,
  ATTRIBUTE_VALUE_TEXT,
  attributePlaceholder
} from '@/components/attributes';
import {
  ATTRIBUTE_BARE_CONTROL,
  ATTRIBUTE_VALUE_OFFSET,
  AttributeEditableCell,
  AttributeRow
} from './attribute-row';
import { formatWithCommas, type useCurrencyInput } from './currency-input';

export function FormFieldCompact({
  label,
  value,
  type = 'text',
  placeholder,
  options = [],
  step,
  onChange,
  onBlur,
  error,
  className,
  currency = false,
  required = false,
  disabled = false,
  clearable = false,
  currencyInput
}: {
  label: string;
  value: string | number | undefined;
  type?: 'text' | 'number' | 'date' | 'textarea' | 'select';
  placeholder?: string;
  options?: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  step?: string;
  onChange?: (value: string | number) => void;
  onBlur?: () => void;
  error?: string;
  className?: string;
  currency?: boolean;
  required?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  /**
   * The currency plumbing, owned by `FormField` and passed in, so the two chromes
   * share **one** `useCurrencyInput` instance rather than each holding its own
   * copy of the same half-typed decimal.
   */
  currencyInput: ReturnType<typeof useCurrencyInput>;
}) {
  const isMoney = currency && type === 'number';

  /**
   * **What the value reads as at rest** — the read-out half of the row (round 6).
   *
   * A money field shows its *formatted* figure with the `$`, exactly as a read-only
   * `FormField` renders it, so `DOD balance` and `BSB` two rows apart are the same
   * kind of thing at rest. A select shows its chosen option's label rather than its
   * stored value, which is what `formatSelectLabel` exists for elsewhere and what a
   * raw `value` would get wrong (`solely-held` instead of `Solely Held`).
   */
  const displayValue = (() => {
    if (value === undefined || value === '') return '';
    if (isMoney) return `$${formatWithCommas(value)}`;
    if (type === 'select') {
      return (
        options.find((opt) => opt.value === String(value))?.label ??
        String(value)
      );
    }
    return String(value);
  })();

  const control = (close: () => void) => {
    if (type === 'select') {
      return (
        <Select
          value={value?.toString() || ''}
          onValueChange={(val) => onChange?.(val === '__clear__' ? '' : val)}
          disabled={disabled}
          /*
            **The select closes the cell, because the cell cannot see it happen.**
            Its list is portalled, so focus leaves this box entirely and the blur
            check cannot tell "chose an option" from "clicked away" — both look the
            same from here. `defaultOpen` means the list is already showing when the
            pointer arrives, which is what the treatment's own select editor does:
            one click to open the row IS one click to open the choices.
          */
          defaultOpen
          onOpenChange={(listOpen) => {
            if (!listOpen) close();
          }}
        >
          <SelectTrigger
            className={cn(ATTRIBUTE_BARE_CONTROL, 'w-full')}
            /*
              The read-out and the placeholder share `ATTRIBUTE_MUTED_INK` on this
              treatment: an unset value shows as `Set <Label>…`-style chrome rather
              than as content, which is the distinction §1.4 draws.
            */
            placeholderColor={ATTRIBUTE_MUTED_INK_PLACEHOLDER}
          >
            <SelectValue
              placeholder={placeholder ?? attributePlaceholder(label)}
            />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
            {clearable && !!value && (
              <>
                <SelectSeparator />
                <SelectItem value="__clear__" className="text-muted-foreground">
                  Clear selection
                </SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      );
    }

    if (type === 'textarea') {
      return (
        <Textarea
          value={value?.toString() || ''}
          onChange={(event) => onChange?.(event.target.value)}
          onBlur={() => onBlur?.()}
          disabled={disabled}
          /*
            Same reason the `Input` below has it: the editor mounts *in place of* the
            read-out the reader just clicked, so without focus the field appears and
            the click has to be made a second time to type. No compact caller passes
            `type="textarea"` yet — this is the branch being made consistent before
            one does, not a live bug.
          */
          autoFocus
          placeholder={placeholder ?? attributePlaceholder(label)}
          className={cn(ATTRIBUTE_BARE_CONTROL, 'min-h-16 resize-none')}
        />
      );
    }

    return (
      <>
        {/*
          The currency marker sits in the cell beside the input rather than
          absolutely positioned inside it with a compensating `pl-7`. Two reasons:
          the figure then starts on the same 5.1px inset as every other value on
          the surface (an inline `$` shifted it 18px), and the marker is chrome —
          the same ink as a label — instead of text inside the field the user is
          typing into.
        */}
        {isMoney && (
          <span
            className={cn(
              ATTRIBUTE_VALUE_TEXT,
              ATTRIBUTE_LEADING,
              ATTRIBUTE_MUTED_INK,
              'shrink-0 pl-1.5'
            )}
            aria-hidden
          >
            $
          </span>
        )}
        <Input
          type={isMoney ? 'text' : type}
          step={step}
          disabled={disabled}
          inputMode={isMoney ? 'decimal' : undefined}
          /*
            The editor opens *over* the value the reader clicked, so it has to be
            focused and ready to type into — otherwise clicking a figure produces a
            box you then have to click again.
          */
          autoFocus
          value={isMoney ? currencyInput.displayValue : value?.toString() || ''}
          onChange={(event) => {
            if (isMoney) {
              currencyInput.handleChange(event.target.value);
              return;
            }
            // An emptied number is cleared, not `0`: `parseFloat('') || 0`
            // committed a real zero when the reader deleted a value (VALE-1505).
            onChange?.(
              type === 'number'
                ? event.target.value.trim() === '' ||
                  Number.isNaN(parseFloat(event.target.value))
                  ? ''
                  : parseFloat(event.target.value)
                : event.target.value
            );
          }}
          onFocus={isMoney ? currencyInput.handleFocus : undefined}
          onBlur={() => {
            if (isMoney) currencyInput.handleBlur();
            onBlur?.();
          }}
          placeholder={placeholder ?? attributePlaceholder(label)}
          className={cn(ATTRIBUTE_BARE_CONTROL, isMoney && 'pl-0')}
        />
      </>
    );
  };

  return (
    <div className={className}>
      <AttributeRow label={label} required={required}>
        {/*
          **A read-out at rest, an editor on click** — the same motion as the rows
          around it (round 6). It used to be a permanently open editor, which left
          `$ 47,980.12` in a bordered box beside a bare `013-456` two rows up; review
          asked for them to be the same fields, and this is that.

          A `select` closes itself: its list is portalled, so focus leaves this box
          entirely and the blur check cannot tell "chose an option" from "clicked
          away". `defaultOpen` means the list is already showing when the pointer
          arrives, which is what the treatment's own select editor does.
        */}
        <AttributeEditableCell
          display={displayValue}
          placeholder={placeholder ?? attributePlaceholder(label)}
          error={!!error}
          /*
            A `select` portals its list, so blur cannot tell "opened my own options"
            from "clicked away" — it closes itself through the `close` it is handed.
            See `AttributeEditableCell`: with blur closing, the list unmounted the
            moment it opened.
          */
          closeOnBlur={type !== 'select'}
          renderEditor={control}
        />
      </AttributeRow>
      {!!error && (
        /*
          Aligned under the value it belongs to rather than under the label, so a
          message about the figure starts where the figure does. The offset is the
          label column plus the row's own `gap-4` — read from the same custom
          property the column is published through, so it cannot drift from it.
        */
        <p className={cn('text-destructive text-12px', ATTRIBUTE_VALUE_OFFSET)}>
          {error}
        </p>
      )}
    </div>
  );
}
